import { t } from "../features/store-language/i18n.ts";
import axios from "axios";

export const UNEXPECTED_ERROR_MESSAGE = "سرور نتوانست درخواست را کامل کند؛ نتیجه عملیات را با تازه‌سازی اطلاعات بررسی کنید.";

const handledHttpErrors = new WeakSet<object>();

export function isCanceledHttpError(error: unknown): boolean {
  return axios.isCancel(error) ||
    (typeof error === "object" && error !== null &&
      ((error as { code?: unknown }).code === "ERR_CANCELED" ||
        (error as { name?: unknown }).name === "AbortError"));
}

export function markHttpErrorHandled(error: unknown): void {
  if (typeof error === "object" && error !== null) {
    handledHttpErrors.add(error);
  }
}

export function isHandledHttpError(error: unknown): boolean {
  return typeof error === "object" &&
    error !== null &&
    handledHttpErrors.has(error);
}

export function formatTraceIdMessage(_traceId?: string | null): string {
  // Keep the compatibility API; correlation IDs belong to diagnostics, not instructions to users.
  return t(UNEXPECTED_ERROR_MESSAGE);
}

export function getHttpFailureMessage(error: unknown): string {
  const failure = error as {
    code?: string;
    config?: { method?: string };
    response?: { status?: number };
  } | null;
  const status = failure?.response?.status;
  const isRead = ["get", "head", "options"].includes(failure?.config?.method?.toLowerCase() ?? "");
  if (status === 401) return t("نشست ورود معتبر نیست یا منقضی شده است؛ دوباره وارد شوید.");
  if (status === 403) return t("اجازه انجام این درخواست را ندارید.");
  if (status === 404) return t("اطلاعات درخواستی پیدا نشد؛ فهرست را تازه‌سازی کنید.");
  if (status === 409) return t("وضعیت فعلی اطلاعات اجازه انجام این درخواست را نمی‌دهد؛ اطلاعات را تازه‌سازی کنید.");
  if (status === 412) return t("نسخه اطلاعات ارسالی معتبر نیست؛ آخرین نسخه را دریافت و تغییرات خود را بررسی کنید.");
  if (status === 413) return t("حجم اطلاعات یا فایل ارسالی بیش از حد مجاز است؛ حجم آن را کاهش دهید.");
  if (status === 415) return t("قالب اطلاعات یا فایل ارسالی پذیرفته نیست؛ از قالب مجاز این بخش استفاده کنید.");
  if (status === 429) return t("تعداد درخواست‌ها بیش از حد مجاز است؛ کمی صبر کنید و دوباره اقدام کنید.");
  if (status === 408 || status === 504 || failure?.code === "ECONNABORTED" || failure?.code === "ETIMEDOUT") {
    return isRead
      ? t("دریافت اطلاعات در زمان مجاز کامل نشد؛ اطلاعات را تازه‌سازی کنید.")
      : t("مهلت دریافت پاسخ تمام شد؛ نتیجه عملیات مشخص نیست. پیش از ارسال دوباره، اطلاعات را تازه‌سازی کنید.");
  }
  if (typeof status === "number" && status >= 500) {
    return isRead
      ? t("سرور نتوانست اطلاعات درخواستی را آماده کند؛ دریافت اطلاعات انجام نشد.")
      : t(UNEXPECTED_ERROR_MESSAGE);
  }
  if (!status && axios.isAxiosError(error)) {
    return isRead
      ? t("پاسخی از سرور دریافت نشد؛ اتصال شبکه را بررسی و اطلاعات را تازه‌سازی کنید.")
      : t("پاسخی از سرور دریافت نشد؛ اتصال شبکه و نتیجه عملیات را پیش از ارسال دوباره بررسی کنید.");
  }
  if (!status && error instanceof Error && !axios.isAxiosError(error) && /[\u0600-\u06ff]/.test(error.message)) {
    return error.message;
  }
  return t("درخواست پذیرفته نشد؛ جزئیات علت در پاسخ سرور موجود نیست.");
}

export function getErrorTraceId(error: any): string | null {
  const responseTraceId = error?.response?.data?.traceId;
  const headerTraceId =
    error?.response?.headers?.["x-correlation-id"] ??
    error?.response?.headers?.["X-Correlation-ID"];
  const traceId = error?.traceId ?? responseTraceId ?? headerTraceId;
  return typeof traceId === "string" && traceId.trim() ? traceId.trim() : null;
}

export function sanitizeUnhandledHttpError(error: any): any {
  const status = error?.response?.status;
  if (typeof status !== "number" || status < 500) {
    return error;
  }

  const traceId = getErrorTraceId(error);
  error.traceId = traceId;
  if (error.response) {
    error.response.data = getHttpFailureMessage(error);
  }
  return error;
}
